import { useSetDefaultCircleMutation } from "@/api/circles-api";
import { useGetAuthenticatedUser } from "@/api/user";
import { CopyIcon } from "@/assets/icons/CopyIcon";
import { DateICon } from "@/assets/icons/DateIcon";
import UserPlaceholder from "@/assets/icons/user-placeholder.svg?react";
import Show from "@/components/Show";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { Circle } from "@/types/circle";
import { formatDate } from "@/utils/date";
import { Link } from "@tanstack/react-router";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import handsImage from "@/assets/images/hands.jpg";
import { generateCircleShareLink } from "@/utils/share";

type CircleCardProps = {
  circle: Circle;
  id?: string;
};

const CircleCard = ({ circle, id }: CircleCardProps) => {
  const { t } = useTranslation();
  const { mutate: setDefaultCircleMutation, isPending } = useSetDefaultCircleMutation();
  const checkboxId = useId();
  const { data: user } = useGetAuthenticatedUser();
  const isOwner = user?.id === circle.owner?.id;

  return (
    <Card
      className={cn(
        "group relative overflow-hidden cursor-pointer shrink-0 flex flex-col justify-between rounded-2xl border-background-second min-h-[480px] w-[295px] pb-0 bg-transparent",
      )}
      style={{
        background: `linear-gradient(0deg, rgba(0, 0, 0, 0.50) 0%, rgba(0, 0, 0, 0.50) 100%), url(${circle.cover_photo?.minio_url ?? handsImage}) lightgray 50% / cover no-repeat`,
      }}
      id={id}
    >
      <div className="flex size-full flex-col text-[--neutrals-gray-02----sand]">
        <div
          className={cn(
            "bg-black bg-opacity-50",
            !circle.is_user_joined && "opacity-0 pointer-events-none",
          )}
          aria-hidden={!circle.is_user_joined}
        >
          <div className="flex items-center justify-between bg-[#444444] bg-opacity-50 p-4">
            <label htmlFor={checkboxId}>{t("circle.defaultCircle")}</label>
            <Checkbox
              id={checkboxId}
              checked={circle.is_default_circle}
              disabled={circle.is_default_circle || isPending}
              onCheckedChange={() => {
                if (!circle.is_default_circle) {
                  setDefaultCircleMutation(circle.id);
                }
              }}
              className="size-5 border-[1.5px] border-white data-[state=checked]:border-white data-[state=checked]:bg-white data-[state=checked]:text-black"
            />
          </div>
        </div>
        <div className="mt-4 flex flex-1 flex-col gap-4 px-6">
          <div className="flex items-center gap-4">
            <DateICon />
            <p className="text-base md:text-lg">
              {t("circle.startOn")} {formatDate(circle.created_at)}
            </p>
          </div>
          <h3 className="font-size-h4 my-auto transition-all duration-500 group-hover:translate-y-10 group-hover:opacity-0">
            {circle.title}
          </h3>
        </div>
      </div>
      <div className="absolute bottom-0 top-auto z-10 w-full translate-y-full transition-all duration-500 group-hover:translate-y-0">
        <div className="mb-5 flex items-center gap-2.5 px-5 ">
          <Avatar className="size-8">
            <AvatarImage src={circle.owner?.image?.minio_url} alt="profile image" />
            <AvatarFallback>
              <UserPlaceholder className="size-full" />
            </AvatarFallback>
          </Avatar>
          <p className="text-sm font-650 text-[#EDEAE4]">
            {t("circle.createdBy")} {circle.owner?.name ?? t("circle.EFB")}
          </p>
        </div>
        <div className="bg-background">
          <Link
            to={!circle.is_user_joined ? `/?circleId=${circle.id}` : `/circle/${circle.id}`}
            aria-label="Go to circle"
            className="h-full w-full"
          >
            <CardContent>
              <div className="flex w-full flex-col gap-4 pt-6">
                <h3 className="font-size-h4">{circle.title}</h3>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center">
                    {circle.top_contributors.map((user, i) => (
                      <Avatar key={i} className="-ms-3 size-9 first-of-type:ms-0">
                        <AvatarImage src={user.image?.minio_url} alt="profile image" />
                        <AvatarFallback>
                          <UserPlaceholder className="size-full" />
                        </AvatarFallback>
                      </Avatar>
                    ))}
                  </div>
                  <p className="font-size-xsmall">
                    {circle.total_joined_users} {t("circle.peopleContributed")}
                  </p>
                </div>

                <dl className="font-size-xsmall contents">
                  <div className="flex flex-row items-center justify-between">
                    <dt>{t("circle.totalDonations")}</dt>
                    <dd className="font-650">
                      {t("toIntlCurrency", {
                        ...circle.total_money,
                        notation: "compact",
                        compactDisplay: "short",
                      })}
                    </dd>
                  </div>
                  <div className="flex flex-row items-center justify-between">
                    <dt>{t("circle.totalPoints")}</dt>
                    <dd className="font-650">{circle.total_points}</dd>
                  </div>
                </dl>
              </div>
            </CardContent>
          </Link>
          <CardFooter className="gap-2 !p-2">
            {circle.is_user_joined ? (
              <>
                <Show when={isOwner}>
                  <Link to={`/circle/${circle.id}`} aria-label="Go to circle" className="w-full">
                    <Button
                      variant="custom"
                      className={
                        "h-full w-full flex-grow rounded-md bg-foreground py-4 !text-base font-bold text-white sm:text-sm md:px-1"
                      }
                    >
                      {t("challenge.edit")}
                    </Button>
                  </Link>
                </Show>

                <button
                  className="ms-auto px-3.5 py-1.5"
                  onClick={() => {
                    navigator.clipboard.writeText(generateCircleShareLink(circle.id, user?.id));
                    toast.success(t("circle.linkCopied"), { position: "top-center" });
                  }}
                >
                  <CopyIcon />
                </button>
              </>
            ) : (
              <Button
                asChild
                variant="custom"
                className={
                  "h-full w-full flex-grow rounded-md bg-foreground py-4 !text-base font-bold text-white sm:text-sm md:px-1"
                }
              >
                <Link to={`/?circleId=${circle.id}`}>{t("circle.joinCircleofHumanity")}</Link>
              </Button>
            )}
          </CardFooter>
        </div>
      </div>
    </Card>
  );
};

export default CircleCard;
